"use client";
import React, {useEffect, useRef, useState, forwardRef, useImperativeHandle} from 'react';
import { createPortal } from 'react-dom';

interface IframeWrapperProps {
  children: React.ReactNode;
  styles?: string;
  onDrop?: (event: React.DragEvent) => void;
}

const IframeWrapper = forwardRef<HTMLIFrameElement, IframeWrapperProps>(({ children, styles, onDrop, ...props }, ref) => {
    const [iframeRef, setIframeRef] = useState<HTMLIFrameElement|null>(null);
    const [iframeBody, setIframeBody] = useState<HTMLElement|null>(null);
    const styleRef = useRef<HTMLStyleElement | null>(null);

    useImperativeHandle(ref, () => iframeRef as HTMLIFrameElement, [iframeRef]);

    const handleLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const iframe = e.target as HTMLIFrameElement
        if (!iframe.contentDocument) return
        const style = document.createElement('style');
        style.textContent = styles || '';
        iframe.contentDocument.head.appendChild(style);
        styleRef.current = style;
        setIframeBody(iframe.contentDocument.body)
        setIframeRef(iframe)
    }

    useEffect(() => {
        if (styleRef.current) {
            styleRef.current.textContent = styles || '';
        }
    }, [styles]);

    useEffect(() => {
        const iframe = iframeRef;
        if (!iframe?.contentWindow) return;

        const resizeObserver = new ResizeObserver(() => {
            if(iframe.contentWindow) {
                const parent = iframe.parentElement;

                if (parent) {
                    parent.style.minHeight = `${iframe.offsetHeight}px`;

                    iframe.style.height = '0px';
                    iframe.style.height = `${iframe.contentWindow.document.documentElement.scrollHeight}px`;

                    parent.style.minHeight = '';
                }
            }
        });

        if (iframe.contentWindow?.document.body) {
            resizeObserver.observe(iframe.contentWindow.document.body);
        }

        return () => resizeObserver.disconnect();
    }, [iframeBody, iframeRef]);

    return (
        <iframe
            ref={setIframeRef}
            srcDoc={`<!DOCTYPE html>`}
            {...props}
            onLoad={handleLoad}
            onDrop={onDrop}
            width="100%"
            style={{ border: 'none' }}
        >
            {iframeBody && createPortal(children, iframeBody)}
        </iframe>
    );
});

IframeWrapper.displayName = 'IframeWrapper';

export default IframeWrapper;